// Product Attributes data layer (Phase 4, P4-WP10) — the TYPED PIVOT that powers
// configurable, filterable catalogue dimensions (WP12). The SINGLE interface between
// the app and public.product_attributes / product_attribute_options /
// product_attribute_values (supabase/migrations/20260703103000_catalogue_media.sql).
//
// MODEL: attributes are first-class rows (max 5 per company, level 1..5), each with a
// controlled option picklist; a product's value is one row per (product, attribute).
// This keeps WP12 filtering to a single indexed predicate per facet — not free EAV.
//
// company_id is owned + frozen by DB triggers; we never send it.

import { supabase } from "./supabaseClient";

const ATTR = "product_attributes";
const OPT = "product_attribute_options";
const VAL = "product_attribute_values";

export const MAX_ATTRIBUTES = 5;

const ATTR_SELECT = "id, name, level, position, product_attribute_options ( id, value, position )";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23505": message = "That name/value already exists."; break;
    case "23514": message = "Level must be between 1 and 5."; break;
    case "23503": message = "The attribute or product does not exist."; break;
    case "42501": message = "You do not have access to this attribute."; break;
    case "PGRST116": message = "Attribute not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

const ATTR_WRITE_FIELDS = ["name", "level", "position"];

// ── Attribute definitions (+ options embedded) ───────────────────────────────

/** All attribute definitions for the company, each with its options, ordered. */
export async function getAttributes() {
  const { data, error } = await client().from(ATTR).select(ATTR_SELECT)
    .order("level", { ascending: true }).order("position", { ascending: true });
  if (error) throw toError(error, "Could not load attributes");
  const rows = data || [];
  rows.forEach((a) => a.product_attribute_options?.sort((x, y) => (x.position || 0) - (y.position || 0)));
  return rows;
}

/** Create a dimension (e.g. "Colour"). Company enforces max 5 via UI; DB allows more. */
export async function createAttribute(payload) {
  const { data, error } = await client().from(ATTR).insert(pick(payload, ATTR_WRITE_FIELDS)).select("id").single();
  if (error) throw toError(error, "Could not create attribute");
  return data.id;
}

export async function updateAttribute(id, payload) {
  const { error } = await client().from(ATTR).update(pick(payload, ATTR_WRITE_FIELDS)).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not update attribute");
  return id;
}

/** Delete a dimension (cascades its options + any product values). */
export async function deleteAttribute(id) {
  const { error } = await client().from(ATTR).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete attribute");
}

// ── Options (controlled picklist per attribute) ──────────────────────────────

export async function addOption(attributeId, value, position = 0) {
  const { data, error } = await client().from(OPT).insert({ attribute_id: attributeId, value, position }).select("id").single();
  if (error) throw toError(error, "Could not add option");
  return data.id;
}

export async function updateOption(id, { value, position }) {
  const { error } = await client().from(OPT).update(pick({ value, position }, ["value", "position"])).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not update option");
  return id;
}

export async function deleteOption(id) {
  const { error } = await client().from(OPT).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete option");
}

// ── Per-product values ───────────────────────────────────────────────────────

/** A product's attribute values as { attribute_id → value }. */
export async function getProductValues(productId) {
  const { data, error } = await client().from(VAL).select("attribute_id, value").eq("product_id", productId);
  if (error) throw toError(error, "Could not load product attributes");
  const map = {};
  (data || []).forEach((r) => { map[r.attribute_id] = r.value; });
  return map;
}

/**
 * Set (or clear) a product's value for one attribute. Empty value clears it.
 * Upserts on the (product_id, attribute_id) unique index.
 */
export async function setProductValue(productId, attributeId, value) {
  const c = client();
  if (value == null || value === "") {
    const { error } = await c.from(VAL).delete().eq("product_id", productId).eq("attribute_id", attributeId);
    if (error) throw toError(error, "Could not clear attribute");
    return;
  }
  const { error } = await c.from(VAL).upsert({ product_id: productId, attribute_id: attributeId, value }, { onConflict: "product_id,attribute_id" });
  if (error) throw toError(error, "Could not save attribute");
}
